import MouseTrap from "mousetrap";
import { CHAT_STATE } from "../../model/Constants";
import ModalMgr from "../../util/ModalMgr";
import { swapChildElements } from "../../util/HTMLElements";
import { DVV_EVENT_TYPE } from "../webrtc/Constants";
import {closeModalRender} from "../templates/renders/closemodal";

/**
 * Custom element for display close confirmation dialog
 * @type {{template, created(), tagName: string, onCancelClick(): void, onOkClick(): void}}
 */
// eslint-disable-next-line object-shorthand
const CloseModalView: any = {
    tagName: "close-modal-view",
    renderer: closeModalRender,
    initialize(options) {
        const ciAppInstance = options.$app;
        const constObj = options.constants;
        const config = ciAppInstance.getUIConfig();
        const appStringProps = config.appStringProps;

        constObj.closeText = ciAppInstance.closeConfirmationText;
        constObj.closeB1Text = ciAppInstance.closeButton1Text || ciAppInstance.closeModalNegativeButtonText;
        constObj.closeB2Text = ciAppInstance.closeButton2Text || ciAppInstance.closeModalPositiveButtonText;
        constObj.butstyle = config.modalButtonStyles;
        constObj.modalText = config.modalTextStyles;
        constObj.endCallBtnText = appStringProps.endCallText || "End call";
        constObj.closeChatAndCallBtnText = appStringProps.endCallAndChatText || "End call & chat";

        options.data = { isCallInProgress: () => ciAppInstance.isCallInProgress() };
    },
    created() {
        this.mouseTrap = new MouseTrap(this.el);
        this.cancelMouseTrap = new MouseTrap(this.el.querySelector(".cancel-button"));
        this.okMouseTrap = new MouseTrap(this.el.querySelector(".ok-button"));
        this.modalFooter = this.el.querySelector(".modal-footer");

        if (this.$app.reverseCloseModalButtons) {
            swapChildElements(this.modalFooter.firstElementChild, this.modalFooter.lastElementChild);
        }

        /**
         * when more than two buttons within modal footer it will displays
         * buttons in column view
         */
        if (this.modalFooter.childElementCount > 2) {
            this.modalFooter.classList.add("modal-column-buttons");
        }

        this.bindKeyboardShortcuts();
        this.setModalVisibility(true);

        // Default focus on ok
        this.el.querySelector(".ok-button").focus();
    },

    /**
     * Keep tracks of Modal visibility in state Manager
     * @param {boolean} visibility - Signal that modal is visible.
     */
    setModalVisibility(visibility) {
        this.$app.getStateMgr().setState({ isCloseConfirmVisible: visibility });
    },

    /**
     * Bind keyboard shortcuts
     */
    bindKeyboardShortcuts() {
        this.mouseTrap.bind("escape", () => this.onCancelClick());
        this.mouseTrap.bind("tab", (e) => ModalMgr.onTab(e, this.el));
        this.mouseTrap.bind("shift+tab", (e) => ModalMgr.onShiftTab(e, this.el));
        this.cancelMouseTrap.bind(["enter", "space"], e => e.target.click());
        this.okMouseTrap.bind(["enter", "space"], e => {
            e.target.click();
            e.preventDefault();
        });
    },

    /**
     *  Click cancel modal button removes the modal from the window
     *  Focuses the Text input once modal is closed
     */
    onCancelClick() {
        this.el.parentElement.removeChild(this.el);
        ModalMgr.removeNegativeTabIndexOnClosingModal();
    },

    /**
     *  This method end the video / audio call and
     *  then close modal window
     */
    onEndCallClick() {
        const videoStreamContainer: any = document.querySelector("video-stream-view");

        if (videoStreamContainer) {
            videoStreamContainer.endCall();
        } else {
            /**
             * This condition will execute when customer decline video/audio call
             * invitation before starting the call , here we are removing container
             * element for web rtc view.
             */

            this.$app.shoutui.getLayoutEl().removeVideoCallContainer(DVV_EVENT_TYPE.CUSTOMER_DECLINED_CALL);
        }

        this.onCancelClick();
    },
    onOkClick() {
        this.$app.isChatDone = true;
        this.$app.getSDKInstance().closeChat();
        this.$app.getStateMgr().setState({ state: CHAT_STATE.ENDED });
        ModalMgr.removeNegativeTabIndexOnClosingModal();
    },
    removed() {
        this.mouseTrap.unbind("escape");
        this.mouseTrap.unbind("tab");
        this.mouseTrap.unbind("shift+tab");
        this.cancelMouseTrap.unbind(["enter", "space"]);
        this.okMouseTrap.unbind(["enter", "space"]);
        this.setModalVisibility(false);
        this.$app.getStateMgr().setState({ isModalVisible: false });
    },
};

/**
 * @export
 * @param   {CIApp} appInstance App instance
 * @return  {CloseModalView}    View instance
 */
export default appInstance => {
    CloseModalView.$app = appInstance;
    const constObj: any = {};
    CloseModalView.constants = constObj;
    return CloseModalView;
};
